import React, { useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import shortid from 'shortid';

// VDS
import Pagination from '@visa/vds-react/pagination-styled';
import PaginationControl from '@visa/vds-react/pagination-control-styled';
import PaginationPage from '@visa/vds-react/pagination-page-styled';

// CYBS
import CybsButton from '@cybs-ui/components/lib/button';
import CybsIcon from '@cybs-ui/components/lib/icon';

import { styles } from './component.styles';


/**
 * A pagination component.
 */
const CybsPagination = ({
  customCSS,
  dataTest,
  i18n: i18nProp,
  jumpToLast,
  onButtonClick,
  pagesToShow,
  selectedPage,
  totalPages
}) => {
  // Use localization defaults for undefined strings
  const i18n = {
    ...CybsPagination.defaultProps.i18n,
    ...i18nProp
  };
  if (totalPages > 5) {
    pagesToShow = pagesToShow > 5 ? pagesToShow : 5;
  }

  const itemsNodes = useRef(null);

  const [id] = useState(shortid.generate());
  const [componentClick, setComponentClick] = useState(false);
  const [pageClick, setPageClick] = useState(false);
  const [_layoutEffect, _withLayoutEffect] = useState(null);
  const withLayoutEffect = fn => _withLayoutEffect(() => fn);

  useLayoutEffect(() => {
    if (_layoutEffect) {
      const save = _layoutEffect;
      withLayoutEffect(null);
      save();
    }
  }, [_layoutEffect]);

  const display = () => {
    function makeNumbers(pageIndex, showJumpToFirst, showJumpToLast) {
      let hardcodedCount = 0;
      if (showJumpToFirst) {
        hardcodedCount += 2;
      }
      if (showJumpToLast) {
        hardcodedCount += 2;
      }
      // Min to handle when totalpages < pagesToShow
      const length = Math.min(pagesToShow, totalPages) - hardcodedCount;
      const output = [];
      if (showJumpToFirst) {
        output.push(
          {
            id: `${id}-1`,
            isActive: false,
            label: 1
          },
          {
            isActive: false,
            label: null
          }
        );
      }
      for (let i = 0; i < length; i++) {
        output.push({
          id: `${id}-${pageIndex + i}`,
          isActive: pageIndex + i === selectedPage,
          label: pageIndex + i
        });
      }
      if (showJumpToLast) {
        output.push(
          {
            isActive: false,
            label: null
          },
          {
            id: `${id}-${totalPages}`,
            isActive: selectedPage === totalPages,
            label: totalPages
          }
        );
      }
      return output;
    }

    // Defensive code
    if (!selectedPage) {
      return [];
    }

    const showJumpToLast =
      jumpToLast && (selectedPage + pagesToShow - 3 <= totalPages);
    const showJumpToFirst =
      selectedPage > pagesToShow ||
      (showJumpToLast && selectedPage >= pagesToShow - 2);
    let pageIndex;
    if (!showJumpToFirst) {
      // close to start, count from 1
      pageIndex = 1;
    } else if (selectedPage - 1 + pagesToShow - 2 > totalPages) {
      // close to end, don't count past max
      pageIndex = totalPages - pagesToShow + 3;
    } else {
      pageIndex = selectedPage - 1;
    }

    return makeNumbers(pageIndex, showJumpToFirst, showJumpToLast);
  };

  const onPageClick = (e, label) => {
    onButtonClick(e, label);
    setComponentClick(true);
    setPageClick(true);
    withLayoutEffect(() => {
      // eslint-disable-next-line no-unused-expressions
      document.getElementById(`${id}-${label}`)?.focus();
    });
  };

  return (
    totalPages > 1 && (
      <Pagination
        ariaLabel={i18n.paginationAriaLabel}
        data-test={`${dataTest}-cybsPagination`}
        css={theme => styles(theme, customCSS).pagination}
        data-total-pages={totalPages}
        ref={itemsNodes}
      >
        <PaginationControl>
          <CybsButton
            key={`${dataTest}-prev-button`}
            autoFocus={selectedPage === 1 && !pageClick && componentClick}
            css={
              selectedPage === 1
                ? theme => styles(theme, customCSS).disabledButton
                : theme => styles(theme, customCSS).button
            }
            tabIndex={selectedPage === 1 ? '-1' : undefined}
            colorScheme="secondary"
            data-test={`${dataTest}-previousButton-cybsPagination`}
            aria-disabled={selectedPage === 1}
            i18n={{
              ariaLabel:
                selectedPage === 1
                  ? i18n.previousPageDisabledAriaLabel
                  : i18n.previousPageAriaLabel
            }}
            iconClass="arrow-previous"
            iconType="light-tiny"
            onClick={
              selectedPage === 1
                ? undefined
                : e => {
                    setComponentClick(true);
                    setPageClick(false);
                    onButtonClick(e, selectedPage - 1);
                  }
            }
            resolution="tiny"
            variant="icon"
          />
        </PaginationControl>
        {display().map(page =>
          page.label ? (
            <PaginationPage selected={page.label === selectedPage}>
              <CybsButton
                id={page.id}
                customCSS={theme => styles(theme, customCSS, page).button}
                variant="text-icon"
                iconType="light-tiny"
                colorScheme="secondary"
                dataTest={`${page.label}-number`}
                aria-disabled={page.isActive}
                tabIndex={page.isActive && '-1'}
                onClick={e => onPageClick(e, page.label)}
                i18n={{
                  label: page.label,
                  ariaLabel: `${i18n.pageAriaLabel} ${page.label}`
                }}
              />
            </PaginationPage>
          ) : (
            <CybsIcon
              dataTest={`${dataTest}-more-icon`}
              ariaLabel={i18n.morePagesAriaLabel}
              name="options"
              resolution="low"
              customCSS={theme => styles(theme).moreIcon}
            />
          )
        )}
        <PaginationControl>
          <CybsButton
            key={`${dataTest}-next-button`}
            autoFocus={
              selectedPage === totalPages && !pageClick && componentClick
            }
            colorScheme="secondary"
            data-test={`${dataTest}-cybsPagination-nextButton`}
            aria-disabled={selectedPage === totalPages}
            css={
              selectedPage === totalPages
                ? theme => styles(theme, customCSS).disabledButton
                : theme => styles(theme, customCSS).button
            }
            tabIndex={selectedPage === totalPages ? '-1' : undefined}
            i18n={{
              ariaLabel:
                selectedPage === totalPages
                  ? i18n.nextPageDisabledAriaLabel
                  : i18n.nextPageAriaLabel,
              toolTipLabel:
                selectedPage === totalPages
                  ? i18n.nextPageDisabledAriaLabel
                  : i18n.nextPageAriaLabel
            }}
            iconClass="arrow-next"
            iconType="light-tiny"
            onClick={
              selectedPage === totalPages
                ? undefined
                : e => {
                    setComponentClick(true);
                    setPageClick(false);
                    onButtonClick(e, selectedPage + 1);
                  }
            }
            resolution="tiny"
            variant="icon"
          />
        </PaginationControl>
      </Pagination>
    )
  );
};

CybsPagination.propTypes = {
  /**
   * DataTest
   */
  dataTest: PropTypes.string.isRequired,
  /**
   * Callback Fired when the button is clicked
   */
  onButtonClick: PropTypes.func,
  /**
   * Total number of pages
   */
  totalPages: PropTypes.number,
  /**
   * Current selecetd page
   */
  selectedPage: PropTypes.number,
  /**
   * Localization
   */
  i18n: PropTypes.shape({
    pageAriaLabel: PropTypes.string,
    paginationAriaLabel: PropTypes,
    morePagesAriaLabel: PropTypes.string,
    nextPageAriaLabel: PropTypes.string,
    previousPageAriaLabel: PropTypes.string,
    nextPageDisabledAriaLabel: PropTypes.string,
    previousPageDisabledAriaLabel: PropTypes.string
  }),
  /**
   * Number of pages to show
   */
  pagesToShow: PropTypes.string,
  /**
   * Show the last item
   */
  jumpToLast: PropTypes.bool
};

CybsPagination.defaultProps = {
  i18n: {
    morePagesAriaLabel: 'More pages',
    nextPageAriaLabel: 'Next page',
    pageAriaLabel: 'Page',
    paginationAriaLabel: 'Pagination',
    previousPageAriaLabel: 'Previous page',
    nextPageDisabledAriaLabel: 'Next Page Disabled',
    previousPageDisabledAriaLabel: 'Previous Page Disabled'
  },
  selectedPage: 1,
  onButtonClick: null,
  totalPages: 1,
  pagesToShow: 5,
  jumpToLast: false
};

CybsPagination.displayName = 'CybsPagination';

export default CybsPagination;
